
import { useState, useMemo } from 'react';
import { AgencyListing } from './useAgencyAnalytics';

type SortDirection = 'asc' | 'desc';

export const useTableSorting = (data: AgencyListing[], itemsPerPage: number = 10) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<keyof AgencyListing>('total_anuncios');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      let aValue: number;
      let bValue: number;

      switch (sortField) {
        case 'total_anuncios':
        case 'valor_total':
        case 'total_bairros':
        case 'total_tipos':
        case 'tempo_medio_anuncio':
          aValue = a[sortField];
          bValue = b[sortField];
          break;
        case 'anunciante':
        case 'lista_tipos':
          // For string fields, use localeCompare
          const aStr = String(a[sortField]);
          const bStr = String(b[sortField]);
          return sortDirection === 'asc' 
            ? aStr.localeCompare(bStr) 
            : bStr.localeCompare(aStr);
        default:
          // For any other numeric field
          aValue = Number(a[sortField]) || 0;
          bValue = Number(b[sortField]) || 0;
      }

      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [data, sortField, sortDirection]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(data.length / itemsPerPage);

  const handleSort = (field: keyof AgencyListing) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  return {
    currentPage,
    setCurrentPage,
    sortField,
    sortDirection,
    handleSort,
    paginatedData,
    totalPages,
  };
};
